import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { PageHeader, Stat, Stats, Empty, Loading } from '../../components/office';
import { localPurchasesApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Signal } from '@verlo/shared/components/ui/signal';
import { Textarea } from '@verlo/shared/components/ui/textarea';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@verlo/shared/components/ui/dialog';

// Local purchases (Back office sweep, Oct 2026). Cash-and-carry and one-off buys. Each says
// what it was and why it couldn't wait for an order, and can carry a photo of the receipt,
// shrunk in the browser before it's sent. The cards open to show the detail.

const money = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v || 0);
const day = (iso) => (iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : '');
const EMPTY = { vendor: '', amount: '', what: '', category: 'General', receipt: null };

// A phone photo is several MB; a receipt reads fine at 1200px as a JPEG.
function shrink(file, max = 1200) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const k = Math.min(1, max / Math.max(img.width, img.height));
      const c = document.createElement('canvas');
      c.width = Math.round(img.width * k);
      c.height = Math.round(img.height * k);
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(img.src);
      resolve(c.toDataURL('image/jpeg', 0.72));
    };
    img.onerror = () => reject(new Error('not an image'));
    img.src = URL.createObjectURL(file);
  });
}

function PurchaseCard({ p, open, onToggle, onDelete }) {
  const [receipt, setReceipt] = useState(null);
  useEffect(() => {
    if (open && p.has_receipt && !receipt) localPurchasesApi.receipt(p.id).then((r) => setReceipt(r.data.receipt)).catch(() => setReceipt(''));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="border-b border-hairline last:border-0" data-testid={`purchase-row-${p.id}`}>
      <button type="button" onClick={onToggle} aria-expanded={open}
        className="grid w-full grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-4 px-5 py-3 text-left hover:bg-hover-tint">
        <span className="flex min-w-0 items-center gap-2">
          {open ? <ChevronDown size={15} className="shrink-0 text-ink-faint" /> : <ChevronRight size={15} className="shrink-0 text-ink-faint" />}
          <span className="min-w-0">
            <span className="block truncate font-semibold text-ink">{p.what || p.vendor}</span>
            <span className="block text-[12.5px] text-ink-soft">{p.what ? `${p.vendor} · ` : ''}{day(p.date)}{p.created_by ? ` · ${p.created_by}` : ''}</span>
          </span>
        </span>
        <span className="flex items-center gap-2">
          <span className="text-[12.5px] text-ink-faint">{p.category}</span>
          {p.has_receipt ? <Signal tone="ok" dot={false}>receipt</Signal> : <Signal tone="warn" dot={false}>no receipt</Signal>}
        </span>
        <span className="w-20 text-right font-mono tabular-nums text-ink">{money(p.amount)}</span>
      </button>
      {open && (
        <div className="flex flex-wrap items-start gap-5 px-5 pb-4 pl-12">
          <dl className="grid min-w-0 flex-1 grid-cols-[110px_minmax(0,1fr)] gap-y-1.5 text-[13.5px]">
            <dt className="text-ink-faint">What and why</dt><dd className="text-ink">{p.what || <span className="text-ink-faint">Not said (logged before it was asked)</span>}</dd>
            <dt className="text-ink-faint">Where</dt><dd className="text-ink">{p.vendor}</dd>
            <dt className="text-ink-faint">Category</dt><dd className="text-ink">{p.category}</dd>
            {p.notes && <><dt className="text-ink-faint">Note</dt><dd className="text-ink">{p.notes}</dd></>}
          </dl>
          {p.has_receipt && (receipt
            ? <a href={receipt} target="_blank" rel="noreferrer"><img src={receipt} alt={`Receipt from ${p.vendor}`} className="h-40 rounded-md border border-hairline object-cover" /></a>
            : <div className="grid h-40 w-28 place-items-center rounded-md border border-hairline text-[12px] text-ink-faint">{receipt === '' ? 'Couldn’t load it' : 'Loading…'}</div>)}
          <div className="w-full text-right">
            <Button variant="ghost" size="sm" className="text-signal-stop hover:text-signal-stop" onClick={onDelete} data-testid={`purchase-delete-${p.id}`}>Delete</Button>
          </div>
        </div>
      )}
    </div>
  );
}

const LocalPurchasesPage = () => {
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [logging, setLogging] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const file = useRef(null);

  const load = () => localPurchasesApi.list()
    .then((r) => setData(r.data || { purchases: [], week_total: 0, month_total: 0, count: 0 }))
    .catch(() => { setData({ purchases: [], week_total: 0, month_total: 0, count: 0 }); toast.error("Couldn't load purchases"); });
  useEffect(() => { load(); }, []);

  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    try { setForm((cur) => ({ ...cur, receipt: null })); const receipt = await shrink(f); setForm((cur) => ({ ...cur, receipt })); }
    catch { toast.error("That doesn't look like a photo"); }
  };

  const save = async () => {
    const amount = parseFloat(form.amount);
    if (!form.what.trim()) { toast.error('Say what it was and why'); return; }
    if (!form.vendor.trim()) { toast.error('Where was it from?'); return; }
    if (!(amount > 0)) { toast.error('How much was it?'); return; }
    setSaving(true);
    try {
      await localPurchasesApi.create({ vendor: form.vendor.trim(), amount, what: form.what.trim(), category: form.category.trim() || 'General', receipt: form.receipt || null });
      toast.success('Purchase logged');
      setLogging(false);
      setForm(EMPTY);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Couldn't log it"); } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.what || p.vendor}, ${money(p.amount)}?`)) return;
    try { await localPurchasesApi.delete(p.id); toast.success('Purchase deleted'); load(); } catch { toast.error("Couldn't delete it"); }
  };

  const purchases = data?.purchases || [];
  const missing = purchases.filter((p) => !p.has_receipt).length;

  return (
    <div className="space-y-5" data-testid="local-purchases-page">
      <PageHeader
        title="Local purchases"
        purpose="Cash-and-carry and one-off buys, so stock and cost of sales stay honest."
        actions={<Button className="bg-verlo hover:bg-verlo-hover" onClick={() => { setForm(EMPTY); setLogging(true); }} data-testid="purchase-add"><Plus size={16} className="mr-2" />Log a purchase</Button>}
      />

      <Stats>
        <Stat label="Last 7 days" value={data ? money(data.week_total) : null} testId="purchase-week-total" />
        <Stat label="This month" value={data ? money(data.month_total) : null} />
        <Stat label="Purchases" value={data ? data.count : null} />
        <Stat label="No receipt" value={data ? missing : null} delta={missing ? { text: 'Ask for them', tone: 'warn' } : undefined} />
      </Stats>

      {data === null ? <Loading shape="table" /> : purchases.length === 0 ? (
        <Empty word="Purchases" title="Nothing bought locally yet" testId="purchase-empty">When someone pops out for lemons, log it here with the receipt.</Empty>
      ) : (
        <section className="rounded-lg border border-hairline bg-white" aria-label="Purchases">
          {purchases.map((p) => <PurchaseCard key={p.id} p={p} open={openId === p.id} onToggle={() => setOpenId(openId === p.id ? null : p.id)} onDelete={() => remove(p)} />)}
        </section>
      )}

      <Dialog open={logging} onOpenChange={setLogging}>
        <DialogContent size="form" data-testid="purchase-dialog">
          <DialogHeader>
            <DialogTitle>Log a purchase</DialogTitle>
            <DialogDescription>Something bought with cash or a card, not through an order.</DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-[12.5px] text-ink-soft">What and why</Label>
              <Textarea rows={2} value={form.what} onChange={(e) => setForm({ ...form, what: e.target.value })} maxLength={300}
                placeholder="e.g. Lemons and limes; the Brakes order came without them" data-testid="purchase-what-input" autoFocus />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-[12.5px] text-ink-soft">Where</Label>
                <Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} placeholder="e.g. Booker, the market…" maxLength={200} data-testid="purchase-vendor-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[12.5px] text-ink-soft">Amount (£)</Label>
                <Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="font-mono" data-testid="purchase-amount-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[12.5px] text-ink-soft">Category</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} maxLength={60} className="w-56" data-testid="purchase-category-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[12.5px] text-ink-soft">Receipt <span className="text-ink-faint">(optional)</span></Label>
              <input ref={file} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid="purchase-receipt-input" />
              {form.receipt ? (
                <div className="flex items-end gap-3">
                  <img src={form.receipt} alt="The receipt" className="h-28 rounded-md border border-hairline object-cover" />
                  <Button variant="ghost" size="sm" onClick={() => setForm({ ...form, receipt: null })}>Remove</Button>
                </div>
              ) : (
                <Button variant="outline" onClick={() => file.current?.click()}>Add a photo of the receipt</Button>
              )}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLogging(false)}>Cancel</Button>
            <Button className="bg-verlo hover:bg-verlo-hover" onClick={save} disabled={saving} data-testid="purchase-save">{saving ? 'Logging…' : 'Log purchase'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LocalPurchasesPage;
